<template>
  <div>
    <!-- Loading indicator -->
    <div v-if="! dataLoaded">
      <v-progress-circular indeterminate color="primary" class="ret-center-absolute"></v-progress-circular>
    </div>
    <!-- Checklist render -->
    <v-card v-if="dataLoaded" class="pa-2 ret-card">
      <v-card-title>
        <h3 class="headline">[{{ editedItem.checklist.code }}] {{ editedItem.checklist.name }}</h3>
        <v-spacer/>
        <!-- Translation selector -->
        <v-select
          v-model="language"
          item-text="name"
          item-value="code"
          :items="availableLanguages"
          :label="$t('translation') "
          class="flex xs6 sm4 md3 lg2"
        ></v-select>
        <!-- Other tools -->
        <v-menu close-on-click>
          <template v-slot:activator="{ on }">
            <v-btn text icon v-on="on">
              <v-icon>more_vert</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="exportToCSV" :disabled="isUnsaved" :title="$t('checklists.download-as-csv')">
              <v-list-item-avatar>
                <v-icon class="mr-2">file_download</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                {{ $t('csv-export') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="exportToPDF" :disabled="isUnsaved" :title="$t('checklists.download-as-pdf')">
              <v-list-item-avatar>
                <v-icon class="mr-2">file_download</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                {{ $t('pdf-export') }}
              </v-list-item-content>
            </v-list-item>
            <v-divider/>
            <v-list-item>
              <v-list-item-avatar>
                <v-icon class="mr-2">input</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-menu open-on-hover offset-y :disabled="!dataFiles.length">
                  <template v-slot:activator="{ on }">
                    <v-btn text class="ml-0 pl-0" v-on="on" :disabled="!dataFiles.length">
                      {{ $t('import-data') }}
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item v-for="file in dataFiles" :key="file" @click="importData(file)">
                      <v-icon class="mr-2">description</v-icon>
                      {{ file }}
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>

      <checklist-section
        id="checklist"
        :level="editedItem.checklist.definition.lvl"
        :index="editedItem.checklist.definition.idx"
        :items="editedItem.checklist.definition.items"
        :label="editedItem.checklist.definition.label"
        :description="editedItem.checklist.definition.description"
      ></checklist-section>

      <checklist-summary v-if="editedItem.checklist.summary"
        :valid="overallValidStatus"
        :saved="isUnsaved === false"
        :status="editedItem.summaryStatus"
        :remarks="editedItem.summaryRemarks"
        @update="updateSummary"
        @save="saveResult({})"
        @back="$router.go(-1)"
      ></checklist-summary>

      <v-card-actions v-else>
        <v-spacer/>
        <v-btn @click="saveResult({})" :disabled="!isUnsaved" color="primary">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<style>
  .outline {
    border: 1px solid #eee;
    border-radius: 2px;
  }
</style>

<script>

import itemHelpers from '../helpers/item-helpers.es6'
import ChecklistSection from './components/checklist-section.vue'
import ChecklistSummary from './components/checklist-summary.vue'
import { PDFExport, CSVExport } from './checklist-export.es6'

export default {
  mixins: [itemHelpers],
  name: 'checklist-in-intervention',
  created () {
    // state changes
    setTimeout(() => {
      this.$store.subscribe(mutation => {
        if (/checklist\/(setValue|importValues|setNote)/.test(mutation.type)) {
          this.isUnsaved = true
        }
        if (/checklist\/save/.test(mutation.type)) {
          this.saveResult({ snackbar: false })
        }
      })
    }, 500)
  },
  data () {
    return {
      model: { name: 'checklists-in-interventions' },
      auxModels: [
        { name: 'files-in-checklists-in-interventions' }
      ]
    }
  },
  computed: {
    language: {
      set (language) {
        this.$store.commit('checklist/setLanguage', { language })
      },
      get () {
        return this.$store.getters['checklist/language']
      }
    },
    availableLanguages () {
      return this.$store.getters['checklist/languages']
    },
    intervention () {
      const item = this.$store.state.item.interventions
      return item.id ? item : null
    },
    asset () {
      const item = this.$store.state.item.assets
      return item.id ? item : null
    },
    images () {
      return this.$store.state.files
    },
    dataFiles () {
      return this.$store.getters['checklist/brcFiles']
    },
    overallValidStatus () {
      const results = this.$store.getters['checklist/results']
      const touched = Object.keys(results).length > 0
      if (!touched) {
        return null
      }
      const invalid = Object.keys(results)
        .map(idx => results[idx].valid)
        .some(valid => valid === false)
      return invalid === false
    }
  },
  methods: {
    async importData (file) {
      try {
        await this.$store.dispatch('checklist/fetchBrcData', {
          assetId: this.intervention.issue.assetId,
          file
        })
        const data = this.$store.getters['checklist/brcData'](file)
        const checklist = this.editedItem.checklist.definition
        this.$store.dispatch('checklist/importValues', { data, checklist })
        console.log('[checklist][import] data', data)
      } catch (error) {
        console.error(error)
      }
    },
    async saveResult ({ reRoute = false, snackbar = true, saveEditedItem = true }) {
      this.editedItem.result = this.cleanJSON(
        this.$store.getters['checklist/results']
      )
      try {
        await this.save({ reRoute, snackbar, saveEditedItem })
        this.isUnsaved = false
      } catch (error) {
        console.error(error)
      }
    },
    async exportToCSV () {
      // create export
      const csv = new CSVExport({
        definition: this.cleanJSON(this.editedItem.checklist.definition),
        results: this.cleanJSON(this.editedItem.result || {}),
        summary: {
          required: this.editedItem.checklist.summary,
          status: this.editedItem.summaryStatus,
          remarks: this.editedItem.summaryRemarks
        },
        translations: {
          'checklists.summary': this.$t('checklists.summary'),
          'checklists.ok': this.$t('checklists.ok'),
          'checklists.ok-with-remarks': this.$t('checklists.ok-with-remarks'),
          'checklists.not-ok': this.$t('checklists.not-ok')
        }
      }).export()

      // download CSV file
      const file = `checklist-${this.editedItem.checklist.code}.csv`
      const link = window.document.createElement('a')
      link.setAttribute('href', 'data:text/csv;charset=utf-8,\uFEFF' + encodeURI(csv))
      link.setAttribute('download', file)
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
    },
    async exportToPDF () {
      try {
        const { assetId } = this.intervention.issue
        // fetch asset
        await this.$store.dispatch('getItem', { modelName: 'assets', id: assetId })
      } catch (error) {
        console.error(error)
      }

      const pdf = new PDFExport({
        definition: this.cleanJSON(this.editedItem.checklist.definition),
        results: this.cleanJSON(this.editedItem.result || {}),
        images: this.images,
        asset: this.asset,
        checklist: this.editedItem.checklist,
        intervention: this.intervention,
        summary: {
          required: this.editedItem.checklist.summary,
          status: this.editedItem.summaryStatus,
          remarks: this.editedItem.summaryRemarks
        },
        translations: {
          'assets.entity-name': this.$tc('assets.entity-name', 1),
          'team': this.$t('team'),
          'date': this.$t('date'),
          'asset-attributes.entity-name': this.$tc('asset-attributes.entity-name', 2),
          'value': this.$t('value'),
          'label': this.$t('label'),
          'description': this.$t('description'),
          'note': this.$t('note'),
          'checklists.summary': this.$t('checklists.summary'),
          'checklists.summary-status': this.$t('checklists.summary-status'),
          'checklists.summary-remarks': this.$t('checklists.summary-remarks'),
          'checklists.ok': this.$t('checklists.ok'),
          'checklists.ok-with-remarks': this.$t('checklists.ok-with-remarks'),
          'checklists.not-ok': this.$t('checklists.not-ok')
        }
      }).export()

      const file = `checklist-${this.editedItem.checklist.code}.pdf`
      pdf.download(file)
    },
    updateSummary ({ status, remarks }) {
      // update summary
      this.editedItem.summaryStatus = status
      this.editedItem.summaryRemarks = remarks
      // lock/unlock checklist
      if (status) {
        this.$store.commit('checklist/lock')
        this.saveResult({})
      } else {
        this.$store.commit('checklist/unlock')
        this.saveResult({ snackbar: false })
      }
    },
    // helpers
    cleanJSON (value) {
      return JSON.parse(JSON.stringify(value))
    }
  },
  watch: {
    item (item) {
      // summary/lock
      if (item.checklist.summary) {
        if (item.summaryStatus) {
          this.$store.commit('checklist/lock')
        } else {
          this.$store.commit('checklist/unlock')
        }
      }

      if (item.checklistId) {
        this.$store.commit('checklist/setInterventionId', { id: item.interventionId })
        this.$store.commit('checklist/setChecklistId', { id: item.checklistId })
      }

      if (item.result === null) {
        // default state
        this.$store.commit('checklist/restoreResults')
      }

      if (item.result) {
        // restore state
        this.$store.commit('checklist/restoreResults', this.cleanJSON(item.result))
      }

      if (!this.intervention) {
        // load intervention
        this.$store.dispatch('getItem', {
          modelName: 'interventions',
          id: this.item.interventionId
        })
      }
    },
    intervention (intervention) {
      this.$store.dispatch('checklist/fetchBrcFiles', { assetId: intervention.issue.assetId })
        .catch(console.warn)
    }
  },
  components: {
    ChecklistSection,
    ChecklistSummary
  }
}
</script>
